import * as React from "react";
import {
  Users,
  DollarSign,
  FolderKanban,
  TrendingUp,
  TrendingDown,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card";

import seedData from "../../../../seed-data.json";

const iconMap: Record<string, LucideIcon> = {
  users: Users,
  "dollar-sign": DollarSign,
  "folder-kanban": FolderKanban,
  "trending-up": TrendingUp,
};

interface OverviewCard {
  id: string;
  label?: string;
  value: string;
  trend: string;
  trendDirection: "up" | "down";
  icon: string;
}

/**
 * OverviewCards - Dashboard metric cards
 *
 * Features:
 * - Clean card layout with semantic tokens
 * - Trend indicators
 * - i18n: labels resolved via useTranslation('dashboard')
 */
export function OverviewCards() {
  const { t } = useTranslation("dashboard");
  const cards = seedData.dashboard.overview.cards as OverviewCard[];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => {
        const Icon = iconMap[card.icon] || TrendingUp;
        const TrendIcon = card.trendDirection === "up" ? TrendingUp : TrendingDown;
        const isPositive = card.trendDirection === "up";

        return (
          <Card key={card.id}>
            <CardContent className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {t(`overview.cards.${card.id}.label`)}
                </span>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-muted">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>

              {/* Value */}
              <div className="mt-4">
                <span className="text-3xl font-semibold tracking-tight text-foreground">
                  {card.value}
                </span>
              </div>

              {/* Trend */}
              <div className="mt-2 flex items-center gap-1.5">
                <div
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                    isPositive ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"
                  }`}
                >
                  <TrendIcon className="h-3 w-3" />
                  <span>{card.trend}</span>
                </div>
                <span className="text-xs text-muted-foreground">vs last month</span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default OverviewCards;
